import { cn } from '@/lib/utils';

interface DivergingBarProps {
  /** Positive (thumbs-up) count. */
  positive: number;
  /** Negative (thumbs-down) count. */
  negative: number;
  className?: string;
}

/**
 * Horizontal diverging bar of positive vs negative share, with a percentage
 * legend. Green = positive, red = negative (success/error tokens). When there
 * are no votes both segments are empty and the muted track shows through.
 */
export function DivergingBar({ positive, negative, className }: DivergingBarProps) {
  const total = positive + negative;
  const pos = total > 0 ? Math.round((positive / total) * 100) : 0;
  const neg = total > 0 ? 100 - pos : 0; // complement so segments always sum to 100%

  return (
    <div className={className}>
      <div
        className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted"
        role="img"
        aria-label={`${pos}% positive, ${neg}% negative`}
      >
        <div className="h-full" style={{ width: `${pos}%`, background: 'var(--success-500)' }} />
        <div className="h-full" style={{ width: `${neg}%`, background: 'var(--error-500)' }} />
      </div>
      <div className="mt-1.5 flex justify-between text-[11px] text-muted-foreground">
        <span>
          <b className={cn('font-semibold text-foreground')}>{pos}%</b> positive
        </span>
        <span>
          <b className={cn('font-semibold text-foreground')}>{neg}%</b> negative
        </span>
      </div>
    </div>
  );
}
